import { Button } from '@components/common';
import { CreditCard, Eye, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import React from 'react';
import type {
  InventorySupplierInvoiceRecord,
  InventorySupplierRecord,
  InventoryStockLocationRecord,
} from '@/renderer/types/inventory';
import {
  INVOICE_STATUS_BADGE_CLASS,
  INVOICE_STATUS_LABELS,
} from './supplierInvoiceForm';

function InvoiceStatusBadge({
  status,
}: {
  status: InventorySupplierInvoiceRecord['status'];
}) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${INVOICE_STATUS_BADGE_CLASS[status]}`}
    >
      {INVOICE_STATUS_LABELS[status]}
    </span>
  );
}

export function SupplierInvoicesTable({
  invoices,
  suppliers,
  locations,
  isLoading = false,
  onView,
  onEdit,
  onDelete,
  onRecordPayment,
}: {
  invoices: InventorySupplierInvoiceRecord[];
  suppliers: InventorySupplierRecord[];
  locations: InventoryStockLocationRecord[];
  isLoading?: boolean;
  onView: (invoice: InventorySupplierInvoiceRecord) => void;
  onEdit: (invoice: InventorySupplierInvoiceRecord) => void;
  onDelete: (invoice: InventorySupplierInvoiceRecord) => void;
  onRecordPayment?: (invoice: InventorySupplierInvoiceRecord) => void;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="relative h-[34rem] overflow-auto">
        {isLoading ? (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center bg-white/65 pt-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 shadow-sm">
              <RefreshCw size={13} className="animate-spin" />
              Updating invoices
            </span>
          </div>
        ) : null}
        <table
          className={`w-full text-sm transition-opacity ${isLoading ? 'opacity-60' : ''}`}
          aria-busy={isLoading}
        >
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Invoice
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Supplier
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Location
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Dates
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Amount
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => {
              const supplier = suppliers.find(
                (s) => s.id === invoice.supplier_id,
              );
              const location = locations.find(
                (l) => l.id === invoice.stock_location_id,
              );
              const outstanding = invoice.total_amount - invoice.amount_paid;
              const isDraft = invoice.status === 'draft';
              const canPay =
                invoice.status === 'posted' ||
                invoice.status === 'partially_paid';

              return (
                <tr key={invoice.id} className="border-t border-gray-100">
                  <td className="px-5 py-4">
                    <div className="font-medium text-gray-900">
                      {invoice.invoice_number || 'No number'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(invoice.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {supplier?.name ?? 'Unknown supplier'}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {location?.name ?? '—'}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    <div>
                      {new Date(invoice.invoice_date).toLocaleDateString()}
                    </div>
                    {invoice.due_date ? (
                      <div className="text-xs text-gray-500">
                        Due {new Date(invoice.due_date).toLocaleDateString()}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-5 py-4">
                    <InvoiceStatusBadge status={invoice.status} />
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="font-medium text-gray-900">
                      {invoice.total_amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    {outstanding > 0 && invoice.status !== 'draft' ? (
                      <div className="text-xs text-orange-600">
                        Outstanding:{' '}
                        {outstanding.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="text"
                        size="sm"
                        onClick={() => onView(invoice)}
                      >
                        <Eye size={14} />
                        View
                      </Button>
                      {isDraft ? (
                        <>
                          <Button
                            variant="text"
                            size="sm"
                            onClick={() => onEdit(invoice)}
                          >
                            <Pencil size={14} />
                            Edit
                          </Button>
                          <Button
                            variant="text"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => onDelete(invoice)}
                          >
                            <Trash2 size={14} />
                            Delete
                          </Button>
                        </>
                      ) : null}
                      {canPay && onRecordPayment ? (
                        <Button
                          variant="text"
                          size="sm"
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={() => onRecordPayment(invoice)}
                        >
                          <CreditCard size={14} />
                          Pay
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
